'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import LanguageToggle from '@/components/ui/LanguageToggle';
import { ShoppingCart, Fuel, ArrowRight, CheckCircle2, Briefcase } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export default function RegisterEntryContent() {
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const handleLangChange = (l: 'en' | 'ar') => {
    setLang(l);
    if (typeof window !== 'undefined') localStorage.setItem('nautex_lang', l);
  };

  const options = [
    {
      id: 'buyer',
      href: '/register/buyer',
      icon: ShoppingCart,
      labelEn: 'Register as Buyer',
      labelAr: 'التسجيل كمشتري',
      descEn: 'Procure marine fuel for your fleet. Submit purchase requests, compare supplier quotes, and manage delivery — all in one platform.',
      descAr: 'شراء الوقود البحري لأسطولك. قدّم طلبات الشراء وقارن عروض الموردين وأدر عمليات التسليم — كل ذلك في منصة واحدة.',
      features: [
        { en: 'Multi-vessel purchase requests', ar: 'طلبات شراء متعددة السفن' },
        { en: 'Side-by-side quote comparison', ar: 'مقارنة العروض جنبًا إلى جنب' },
        { en: 'Delivery confirmation & BDN management', ar: 'تأكيد التسليم وإدارة BDN' },
        { en: 'Full audit trail on every decision', ar: 'سجل تدقيق كامل لكل قرار' },
      ],
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/25',
      hoverBorder: 'hover:border-blue-500/50',
      btnClass: 'btn-secondary',
    },
    {
      id: 'supplier',
      href: '/register/supplier',
      icon: Fuel,
      labelEn: 'Register as Supplier',
      labelAr: 'التسجيل كمورد',
      descEn: 'Supply marine fuel to verified buyers. Get targeted RFQ invitations based on your port coverage and product capabilities.',
      descAr: 'توريد الوقود البحري للمشترين المعتمدين. احصل على دعوات طلبات عروض الأسعار المستهدفة بناءً على تغطية موانئك وقدراتك.',
      features: [
        { en: 'Targeted RFQ invitations by port & grade', ar: 'دعوات RFQ مستهدفة حسب الميناء والدرجة' },
        { en: 'Structured quote submission', ar: 'تقديم عروض أسعار منظمة' },
        { en: 'Delivery schedule & BDN uploads', ar: 'جدول التسليم وتحميل BDN' },
        { en: 'Transparent commission tracking', ar: 'تتبع العمولة بشفافية' },
      ],
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/25',
      hoverBorder: 'hover:border-emerald-500/50',
      btnClass: 'btn-primary',
    },
    {
      id: 'broker',
      href: '/register/broker',
      icon: Briefcase,
      labelEn: 'Register as Broker',
      labelAr: 'التسجيل كوسيط',
      descEn: 'Act as a procurement agent for buyer offices. Raise PRs on their behalf and earn sub-commission on every closed PO.',
      descAr: 'تصرف كوكيل مشتريات لمكاتب المشترين. أنشئ طلبات الشراء نيابةً عنهم واكسب عمولة فرعية على كل أمر شراء مغلق.',
      features: [
        { en: 'Raise PRs on behalf of buyer offices', ar: 'إنشاء طلبات الشراء نيابةً عن المشترين' },
        { en: 'Track all PRs and POs in one portal', ar: 'تتبع جميع الطلبات وأوامر الشراء' },
        { en: 'Sub-commission earnings per closed PO', ar: 'أرباح عمولة فرعية لكل أمر شراء مغلق' },
        { en: 'Transparent settlement dashboard', ar: 'لوحة تسوية شفافة' },
      ],
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/25',
      hoverBorder: 'hover:border-amber-500/50',
      btnClass: 'bg-amber-500 hover:bg-amber-400 text-black',
    },
  ];

  return (
    <div className="min-h-screen nautex-gradient-bg flex flex-col" dir={dir}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4">
        <Link href="/" className="flex items-center gap-2">
          <AppLogo size={32} />
          <span className="font-bold text-sm text-foreground tracking-tight">NautEX</span>
        </Link>
        <LanguageToggle lang={lang} onChange={handleLangChange} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10 max-w-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/8 text-primary text-xs font-medium mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            {lang === 'ar' ? 'منصة شراء وقود السفن البحرية' : 'Maritime Bunker Procurement Platform'}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {lang === 'ar' ? 'كيف تريد الانضمام؟' : 'How would you like to join?'}
          </h1>
          <p className="text-secondary-foreground">
            {lang === 'ar' ?'اختر نوع حسابك للبدء. يمكنك دائمًا التوسع لاحقًا.' :'Choose your account type to get started. You can always expand later.'}
          </p>
        </div>

        {/* Option cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-5xl">
          {options.map(opt => {
            const Icon = opt.icon;
            return (
              <div
                key={opt.id}
                className={`rounded-2xl p-8 border-2 ${opt.border} ${opt.hoverBorder} bg-card/60 transition-all duration-200 flex flex-col`}
              >
                <div className={`w-12 h-12 rounded-xl ${opt.bg} flex items-center justify-center mb-5`}>
                  <Icon size={24} className={opt.color} />
                </div>
                <h2 className="text-xl font-bold text-foreground mb-3">
                  {lang === 'ar' ? opt.labelAr : opt.labelEn}
                </h2>
                <p className="text-sm text-secondary-foreground mb-6 leading-relaxed">
                  {lang === 'ar' ? opt.descAr : opt.descEn}
                </p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {opt.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-secondary-foreground">
                      <CheckCircle2 size={15} className={`${opt.color} mt-0.5 flex-shrink-0`} />
                      {lang === 'ar' ? f.ar : f.en}
                    </li>
                  ))}
                </ul>
                <Link
                  href={opt.href}
                  className={`${opt.btnClass} inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold w-full`}
                >
                  {lang === 'ar' ? opt.labelAr : opt.labelEn}
                  <ArrowRight size={16} />
                </Link>
              </div>
            );
          })}
        </div>

        {/* Sign in link */}
        <p className="mt-8 text-sm text-muted-foreground text-center">
          {lang === 'ar' ? 'لديك حساب بالفعل؟' : 'Already have an account?'}{' '}
          <Link href="/login" className="text-primary hover:text-primary/80 transition-colors font-medium">
            {lang === 'ar' ? 'تسجيل الدخول' : 'Sign in →'}
          </Link>
        </p>
      </div>
    </div>
  );
}
